import { Driver, driver, DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import "../css/driver-tour.css";
import { useEffect } from "react";
import { t } from "../helpers/locale";
import { tKey } from "../helpers/i18n";
import { HGNavigate } from "../helpers/navigate";
import { getStoredBoolean, storeBoolean } from "../helpers/dateStored";

// Store tour state outside component to persist across remounts
let tourHasLaunched = false;
let wmTourHasLaunched = false;
let smpwTourHasLaunched = false;

export function HourglassTour(props: { userId: number }) {
  // Initialize driver once and handle cleanup properly
  useEffect(() => {
    // Early return if tour already launched or shouldn't be shown
    if (tourHasLaunched) {
      return;
    }

    // Mark as launched immediately to prevent duplicate initialization
    tourHasLaunched = true;
    const isDarkMode = document.documentElement.getAttribute("data-bs-theme") === "dark";

    // Create a fresh driver instance
    const driverInstance = driver({
      animate: true,
      showProgress: true,
      showButtons: ["next", "previous", "close"],
      nextBtnText: t("tour.button.next"),
      prevBtnText: t("general.go-back"),
      doneBtnText: t("tour.button.exit"),
      allowClose: true,
      //overlayClickNext: false,
      popoverClass: isDarkMode ? "driver-dark-theme" : "",
      onHighlightStarted: (element) => {
        if (element && element.id) {
          if (["publishers-nav", "reports-nav", "scheduling-nav", "cong-nav"].includes(element.id)) {
            const button = document.querySelector(`#${element.id} button`) as HTMLButtonElement;
            button?.click();
          }
        }
      },
    });

    // Define steps and start tour
    const steps = getTourSteps(props.userId, driverInstance);
    driverInstance.setSteps(steps);
    // Use a small timeout to ensure DOM is ready
    setTimeout(() => {
      driverInstance.drive();
    }, 100);
  }, [props.userId]);

  return null;
}

function pubDetailsText(): string {
  return `<p>${t("tour.pubdetails.intro")}</p>
    <p>${t("tour.pubdetails.invitations")} <b class="ms-1 me-1">  ${t("tour.pubdetails.invitations-security")}</b> ${t(
      "tour.pubdetails.invitations-multiple",
    )} </p>`;
}

function pubMenuText(): string {
  return `<ul>
    <li><b>${t("nav.publishers.add")}</b> ${t("tour.pubmenu.add")}</li>
    <li><b>${t("nav.publishers.send-reminders")}</b> ${t("tour.pubmenu.send-reminders")}</li>
    <li><b>${t("nav.publishers.record-cards")}</b> ${t("tour.pubmenu.record-cards")}</li>
    <li><b>${t("nav.publishers.not-publishing")}</b> ${t("tour.pubmenu.not-publishing")}</li>
    <li><b>${t("nav.publishers.transfer")}</b> ${t(tKey("docs.pubtransfer.1"))}</li>
    <li><b>${t("nav.publishers.contacts.all-publishers")}</b> ${t("tour.pubmenu.all-publishers")}</li>
    <li><b>${t("nav.publishers.contacts.emergency")}</b> ${t("tour.pubmenu.emergency")}</li>
    </ul>`;
}

function reportsMenuText(): string {
  return `<ul>
  <li><b>${t("chart.legend.missing")}</b> ${t("tour.reportsmenu.missing")}</li>
  <li><b>${t("nav.reports.monthly.summary")}</b> ${t("tour.reportsmenu.monthly.summary")}</li>
  <li><b>${t("nav.reports.monthly.detail")}</b> ${t("tour.reportsmenu.monthly.detail")}</li>
  <li><b>${t("list.congregation.title")}</b> ${t("tour.reportsmenu.averages.congregation")}</li>
  <li><b>${t("nav.reports.averages.group")}</b> ${t("tour.reportsmenu.averages.group")}</li>
  <li><b>${t("list.publishers.title")}</b> ${t("tour.reportsmenu.averages.publisher")}</li>
  <li><b>${t("recordcard.svcyeartotals.regular-pioneers")}</b> ${t("tour.reportsmenu.pioneers")}</li>
  <li><b>${t("nav.reports.cong-analysis")}</b> ${t("tour.reportsmenu.cong-analysis")}</li>
  <li><b>${t("nav.reports.svcyeartotals")}</b> ${t("tour.reportsmenu.svcyeartotals")}</li>
</ul>`;
}

function schedulingMenuText(): string {
  return `<ul>
  <li><b>${t("conganalysis.attendance.midweek")}</b> ${t("www.features.scheduling.midweek")}</li>
  <li>
    <b>${t("conganalysis.attendance.weekend")}</b>
    ${t("www.features.scheduling.weekend")} <br/>
    ${t("www.features.scheduling.public-talks")}
  </li>
  <li><b>${t("schedules.ava.title")}</b> ${t("www.features.scheduling.ava")}</li>
  <li><b>${t("schedules.cleaning.title")}</b>/<b>${t("schedules.field-service.title")}</b> ${t(
    "www.features.scheduling.fs-cleaning",
  )}</li>
  <li><b>${t("schedules.public-witnessing.title")}</b> ${t("www.features.scheduling.public-witnessing")}</li>
  <li><b>${t("schedules.privileges.title")}</b> ${t("www.features.scheduling.privileges")}</li>
  <li><b>${t("schedules.absence.title")}</b> ${t("www.features.scheduling.absences")}</li>
  <li><b>${t("schedules.events.title")}</b>
    <ul>
      <li>${t("schedules.events.co-visit")}</li>
      <li>${t("schedules.events.circuit-assembly")}</li>
      <li>${t("schedules.events.regional-convention")}</li>
      <li>${t("schedules.events.memorial")}</li>
    </ul>
  </li>
</ul>`;
}

function congMenuText(): string {
  return `<ul>
  <li><b>${t("nav.congregation.fsgroups")}</b> ${t("tour.congmenu.fsgroups")}</li>
  <li><b>${t("nav.congregation.attendance")}</b> ${t("tour.congmenu.attendance")}</li>
  <li><b>${t("nav.congregation.profile-permissions")}</b> ${t("tour.congmenu.profile-permissions")}</li>
  <li><b>${t("nav.congregation.import")}</b> ${t("tour.congmenu.import")}</li>
  <li><b>${t("nav.congregation.export")}</b> ${t("tour.congmenu.export")}</li>
</ul>`;
}

export function WMTour() {
  // Initialize driver once and handle cleanup properly
  useEffect(() => {
    if (getStoredBoolean("wm_tour")) return;

    // Early return if tour already launched or shouldn't be shown
    if (wmTourHasLaunched) {
      return;
    }

    // Mark as launched immediately to prevent duplicate initialization
    wmTourHasLaunched = true;
    storeBoolean("wm_tour", wmTourHasLaunched);
    const isDarkMode = document.documentElement.getAttribute("data-bs-theme") === "dark";

    // Create a fresh driver instance
    const driverInstance = driver({
      animate: true,
      showProgress: true,
      showButtons: ["next", "previous", "close"],
      nextBtnText: t("tour.button.next"),
      prevBtnText: t("general.go-back"),
      doneBtnText: t("tour.button.exit"),
      allowClose: true,
      popoverClass: isDarkMode ? "driver-dark-theme" : "",
    });

    // Define steps and start tour
    const steps = getWMTourSteps();
    driverInstance.setSteps(steps);
    // Use a small timeout to ensure DOM is ready
    setTimeout(() => {
      driverInstance.drive();
    }, 100);
  }, []);

  return null;
}

function getWMTourSteps(): DriveStep[] {
  return [
    {
      popover: {
        title: t("conganalysis.attendance.weekend"),
        description: t("schedules.weekend.tour.intro"),
        align: "center",
      },
    },
    {
      element: "a.nav-link[data-rr-ui-event-key='schedule']",
      popover: {
        title: t("schedules.schedule"),
        description: t("schedules.weekend.tour.schedule"),
        side: "bottom",
        align: "center",
      },
    },
    {
      element: "a.nav-link[data-rr-ui-event-key='cong_speakers']",
      popover: {
        title: t("schedules.weekend.cong-speakers"),
        description: t("schedules.weekend.tour.cong-speakers"),
        side: "bottom",
        align: "center",
      },
    },
    {
      element: "a.nav-link[data-rr-ui-event-key='stats']",
      popover: {
        title: t("nav.reports.monthly.statistics"),
        description: t("schedules.weekend.tour.stats"),
        side: "bottom",
        align: "center",
      },
    },
    {
      element: "a.nav-link[data-rr-ui-event-key='talks']",
      popover: {
        title: t("schedules.weekend.outlines"),
        description: t("schedules.weekend.tour.outlines"),
        side: "bottom",
        align: "center",
      },
    },
    {
      element: "a.nav-link[data-rr-ui-event-key='data']",
      popover: {
        title: t("schedules.weekend.data"),
        description: t("schedules.weekend.tour.data"),
        side: "bottom",
        align: "center",
      },
    },
    {
      element: "#settings_button",
      popover: {
        title: t("general.settings"),
        description: t("schedules.weekend.tour.settings"),
        side: "left",
        align: "center",
      },
    },
    {
      element: "#print_button",
      popover: {
        title: t("general.print"),
        description: t("schedules.weekend.tour.print"),
        side: "left",
        align: "center",
      },
    },
  ];
}

function getTourSteps(userId: number, driverInstance: Driver): DriveStep[] {
  const youtubeIntroIFrame =
    '<iframe width="560" height="315" src="https://www.youtube.com/embed/WAbQKpN7zrM" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>';

  return [
    {
      popover: {
        title: t("tour.title"),
        description: t("tour.welcome"),

        align: "center",
      },
    },
    {
      element: "#user-search",
      popover: {
        title: t("list.publishers.title"),
        description: "<p>" + t("tour.userlist.main") + "</p>",
        side: "bottom",
        align: "center",
        onNextClick: () => {
          HGNavigate.navigateInstance && HGNavigate.navigateInstance(`/user/${userId}`);
          return new Promise((resolve) => {
            setTimeout(() => {
              driverInstance.moveNext();
              resolve(0);
            }, 500);
          });
        },
      },
    },
    {
      element: "#reports-list-table",
      popover: {
        title: t("nav.reports.0"),
        description: t("tour.reports.main"),
        side: "top",
        align: "center",
      },
    },
    {
      element: "#publisher-summary-card",
      popover: {
        title: t("tour.pubdetails.title"),
        description: pubDetailsText(),
        side: "bottom",
        align: "center",
      },
    },
    {
      element: "#publishers-nav",
      popover: {
        title: t("list.publishers.title"),
        description: pubMenuText(),
        side: "right",
        align: "start",
      },
    },
    {
      element: "#reports-nav",
      popover: {
        title: t("nav.reports.0"),
        description: reportsMenuText(),
        side: "right",
        align: "start",
      },
    },
    {
      element: "#scheduling-nav",
      popover: {
        title: t("schedules.scheduling"),
        description: schedulingMenuText(),
        side: "right",
        align: "start",
      },
    },
    {
      element: "#cong-nav",
      popover: {
        title: t("list.congregation.title"),
        description: congMenuText(),
        side: "right",
        align: "start",
      },
    },
    {
      popover: {
        title: t("tour.title"),
        description: `<p class="mb-4">${t("tour.finished")}</p>` + youtubeIntroIFrame,
        align: "center",
        popoverClass: "tour-end",
      },
    },
  ];
}

export function SMPWAdminTour() {
  // Initialize driver once and handle cleanup properly
  useEffect(() => {
    if (getStoredBoolean("smpw_admin_tour")) return;

    // Early return if tour already launched or shouldn't be shown
    if (smpwTourHasLaunched) {
      return;
    }

    // Mark as launched immediately to prevent duplicate initialization
    smpwTourHasLaunched = true;
    storeBoolean("smpw_admin_tour", smpwTourHasLaunched);
    const isDarkMode = document.documentElement.getAttribute("data-bs-theme") === "dark";

    // Create a fresh driver instance
    const driverInstance = driver({
      animate: true,
      showProgress: true,
      showButtons: ["next", "previous", "close"],
      nextBtnText: t("tour.button.next"),
      prevBtnText: t("general.go-back"),
      doneBtnText: t("tour.button.exit"),
      allowClose: true,
      popoverClass: isDarkMode ? "driver-dark-theme" : "",
    });

    // Define steps and start tour
    const steps = getSMPWAdminTourSteps(driverInstance);
    driverInstance.setSteps(steps);
    // Use a small timeout to ensure DOM is ready
    setTimeout(() => {
      driverInstance.drive();
    }, 100);
  }, []);

  return null;
}

function getSMPWAdminTourSteps(driverInstance: Driver): DriveStep[] {
  const handleOnNextClick = (key: string) => {
    const settingsTab = document.querySelector(key) as HTMLAnchorElement;
    settingsTab?.click();
    return new Promise((resolve) => {
      setTimeout(() => {
        driverInstance.moveNext();
        resolve(0);
      }, 250);
    });
  };

  return [
    {
      popover: {
        title: t("smpw.smpw"),
        description: t("smpw.tour.1"),
        align: "center",
        onNextClick: () => handleOnNextClick("a.nav-link[data-rr-ui-event-key='0']"),
      },
    },
    {
      element: "a.nav-link[data-rr-ui-event-key='0']",
      popover: {
        title: t("smpw.zones"),
        description: t("smpw.tour.2"),
        side: "bottom",
        align: "center",
      },
    },
    {
      element: "#manage_zones",
      popover: {
        title: t("smpw.zones"),
        description: t("smpw.tour.3"),
        side: "top",
        align: "center",
      },
    },
    {
      element: "a.nav-link[data-rr-ui-event-key='1']",
      popover: {
        title: t("list.publishers.title"),
        description: t("smpw.tour.4"),
        side: "bottom",
        align: "center",
      },
    },
    {
      element: "a.nav-link[data-rr-ui-event-key='2']",
      popover: {
        title: t("tags.title"),
        description: t("smpw.tour.5"),
        side: "bottom",
        align: "center",
      },
    },
    {
      element: "a.nav-link[data-rr-ui-event-key='3']",
      popover: {
        title: t("smpw.statistics"),
        description: t("smpw.tour.6"),
        side: "bottom",
        align: "center",
        onNextClick: () => handleOnNextClick("a.nav-link[data-rr-ui-event-key='4']"),
      },
    },
    {
      element: "a.nav-link[data-rr-ui-event-key='4']",
      popover: {
        title: t("general.settings"),
        description: t("smpw.tour.7"),
        side: "bottom",
        align: "center",
      },
    },
    {
      element: "#spiritual_point_card",
      popover: {
        title: t("smpw.spiritual_point"),
        description: t("smpw.tour.8"),
        side: "top",
        align: "center",
      },
    },
    {
      element: "#zone_settings",
      popover: {
        title: t("smpw.zone_settings"),
        description: t("smpw.tour.9"),
        side: "top",
        align: "center",
      },
    },
    {
      element: "#email_templates",
      popover: {
        title: t("smpw.email_templates"),
        description: t("smpw.tour.10"),
        side: "top",
        align: "center",
        onNextClick: () => handleOnNextClick("a.nav-link[data-rr-ui-event-key='0']"),
      },
    },
  ];
}
